// UI-NAV-1 (2026-05-07) — App.js refactored to use the new AppShell.
// GLOBAL-NAV-1 (2026-06-09) — routing now rides the NavigationContext spine:
//   a real history stack (Back/Forward) + a keep-alive page cache (pages stay
//   mounted while "alive" so their state survives Back). The page-render switch
//   moved into renderPage(loc); the 28 onNavigateToClaim consumers are unchanged.
import { useState, useEffect, useCallback, useReducer } from "react";
import { getUser, apiFetch, canViewAdminTab, canViewOperations, canViewAccounting, canViewAnalytics, canAny, can, refreshPermissions } from "./api";
import STYLE from "./styles";
import AppShell from "./components/AppShell";
import LoginPage from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Workbench from "./pages/Workbench";
import ClaimDetail from "./pages/ClaimDetail";
import MyNumbers from "./pages/MyNumbers";
import Reports from "./pages/Reports";
import Operations from "./pages/Operations";
import Accounting from "./pages/Accounting";
import Analytics from "./pages/Analytics";
import Profile from "./pages/Profile";
import Admin from "./pages/Admin";
import SystemAdmin from "./pages/SystemAdmin";
import Companies from "./pages/Companies";
import Contacts from "./pages/Contacts";
import Directory from "./pages/Directory";
import Planner from "./pages/Planner";
import Policies from "./pages/Policies";
import TimeEntryModal from "./components/TimeEntryModal";
import TaskModal from "./components/TaskModal";
import { NavigationProvider, useNavigation } from "./components/nav/NavigationContext";
import KeepAliveOutlet from "./components/nav/KeepAliveOutlet";
import { InvoiceWorkspace } from "./components/InvoicePrep";
import { MyInbox } from "./components/BillIntake";

export default function App() {
  const [user, setUser] = useState(getUser);
  if (!user) return (<><style>{STYLE}</style><LoginPage onLogin={setUser} /></>);
  // The nav spine wraps the whole app; flushes its keep-alive cache + history
  // whenever the logged-in user changes (login / logout / impersonation).
  return (
    <NavigationProvider userId={user.id}>
      <AppInner user={user} setUser={setUser} />
    </NavigationProvider>
  );
}

function AppInner({ user, setUser }) {
  const { current, alive, navigate, back } = useNavigation();
  const page = current.page;

  const [reviewCount, setReviewCount] = useState(0);

  // OPS-1: global quick-create modals (one instance each, app-level) + a "work
  // refresh" counter that surfaces (Home, notification bell) bump on save.
  const [timeModal, setTimeModal] = useState(null);
  const [taskModal, setTaskModal] = useState(null);
  const [workRefresh, setWorkRefresh] = useState(0);
  const openTime = useCallback((opts = {}) => setTimeModal(opts), []);
  const openTask = useCallback((opts = {}) => setTaskModal(opts), []);
  const bumpWork = useCallback(() => setWorkRefresh(n => n + 1), []);

  // Keep cached capabilities fresh (re-fetch on load + window focus); re-render
  // only when caps actually changed.
  const [, forcePermsRerender] = useReducer(x => x + 1, 0);
  useEffect(() => {
    let alive2 = true;
    const sync = () => refreshPermissions().then(r => { if (alive2 && r.changed) forcePermsRerender(); });
    sync();
    window.addEventListener("focus", sync);
    return () => { alive2 = false; window.removeEventListener("focus", sync); };
  }, [user]);

  // Site-wide staleness net (2026-06-26): when the user returns to this tab or
  // window — including the second app window opened from the nav bar — bump the
  // work-refresh counter so every refreshKey-bound surface (Attention panels,
  // notification bell, Workbench grid, queues, financial summaries) refetches.
  // This is the general answer to "a change made elsewhere shows stale until I
  // hit refresh": per-mutation onWorkChanged handles same-view updates; this
  // covers cross-window / cross-surface drift. Throttled so a flurry of focus
  // events can't spam the API.
  useEffect(() => {
    let last = 0;
    const bump = () => {
      const now = Date.now();
      if (now - last < 4000) return;   // at most once per 4s
      last = now;
      bumpWork();
    };
    const onVis = () => { if (!document.hidden) bump(); };
    window.addEventListener("focus", bump);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", bump);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [bumpWork]);

  // Capability gates — protect the render switch from a forbidden page even if
  // an in-app callback navigates to it. AppShell does its own nav-visibility gating.
  const canAdminNav     = canViewAdminTab();
  const canSysadminNav  = can("view_sysadmin_panel");
  const canOpsNav       = canViewOperations();
  const canAccountingNav = canViewAccounting();
  const canAnalyticsNav = canViewAnalytics();
  const canReviewQueue  = canAny(["approve_companies", "approve_contacts"]);

  const refreshReviewCount = useCallback(() => {
    if (!canReviewQueue) { setReviewCount(0); return; }
    apiFetch("/admin/review-count")
      .then(r => setReviewCount((r.companies ?? 0) + (r.contacts ?? 0)))
      .catch(() => {/* non-fatal */});
  }, [canReviewQueue]);
  useEffect(() => { refreshReviewCount(); }, [page, refreshReviewCount]);

  // ── Navigation wrappers (thin shims over the context; same signatures the 28
  //    onNavigateToClaim/setPage/onBack consumers already expect) ──
  const navigateToClaim = useCallback((claimId, focus = null) =>
    navigate({ page: "claim-detail", entityType: "claim", entityId: claimId, params: { claimId, focus } }), [navigate]);
  // Scope E: a notification for a claimless document/bill deep-links to the
  // Operations Document Review hub, focused on that item.
  const navigateToBillReview = useCallback((billId) =>
    navigate({ page: "operations", params: { opTab: "billreview", focusBillId: billId } }), [navigate]);
  const navigateToContact = useCallback((contactId) =>
    navigate({ page: "contacts", params: { contactId } }), [navigate]);
  const navigateToCompany = useCallback((companyId) =>
    navigate({ page: "companies", params: { companyId } }), [navigate]);
  const setPage = useCallback((id) => navigate({ page: id }), [navigate]);
  const resetSelection = useCallback(() => {}, []);   // selection now lives per-location

  // ── Render a single location's page (props derived from loc.params). Page
  //    components are unchanged; keep-alive keeps them mounted across Back. ──
  const renderPage = useCallback((loc) => {
    const p = loc.params || {};
    switch (loc.page) {
      case "dashboard":   return <Dashboard user={user} onNavigateToClaim={navigateToClaim} openTime={openTime} openTask={openTask} workRefresh={workRefresh} setPage={setPage} />;
      case "planner":     return <Planner onNavigateToClaim={navigateToClaim} openTask={openTask} workRefresh={workRefresh} />;
      case "claims":      return <Workbench onSelectClaim={(id) => navigateToClaim(id)} currentUser={user} workRefresh={workRefresh} />;
      case "myinbox":     return <MyInbox currentUser={user} onNavigateToClaim={navigateToClaim} onWorkChanged={bumpWork} workRefresh={workRefresh} />;
      case "claim-detail":return <ClaimDetail claimId={p.claimId} onBack={back} openTime={openTime} openTask={openTask} onWorkChanged={bumpWork} workRefresh={workRefresh} initialTab={p.focus?.tab} focusBillId={p.focus?.billId} focusTaskId={p.focus?.taskId} />;
      case "directory":   return <Directory currentUser={user} onNavigateToCompany={navigateToCompany} onNavigateToContact={navigateToContact} onNavigateToClaim={navigateToClaim} />;
      case "companies":   return <Companies currentUser={user} onNavigateToClaim={navigateToClaim} onNavigateToContact={navigateToContact} focusCompanyId={p.companyId} onFocusHandled={() => {}} />;
      case "contacts":    return <Contacts currentUser={user} onReviewCountChanged={refreshReviewCount} onNavigateToClaim={navigateToClaim} initialContactId={p.contactId} onClearInitial={() => {}} />;
      case "reports":     return <Reports onSelectClaim={navigateToClaim} />;
      case "operations":  return canOpsNav ? <Operations openTime={openTime} onWorkChanged={bumpWork} onNavigateToClaim={navigateToClaim} initialTab={p.opTab} focusBillId={p.focusBillId} /> : null;
      case "accounting":  return canAccountingNav ? <Accounting openTime={openTime} onWorkChanged={bumpWork} onNavigateToClaim={navigateToClaim} /> : null;
      case "invoice":     return canOpsNav ? <InvoiceWorkspace invId={p.invId} onChanged={() => {}} onClose={back} onNavigateToClaim={navigateToClaim} /> : null;
      case "analytics":   return canAnalyticsNav ? <Analytics user={user} onNavigateToClaim={navigateToClaim} /> : null;
      case "policies":    return can("view_company_policies") ? <Policies /> : null;
      case "mynumbers":   return <MyNumbers user={user} />;
      case "profile":     return <Profile user={user} />;
      case "admin":       return canAdminNav ? <Admin currentUser={user} onReviewCountChanged={refreshReviewCount} onNavigateToClaim={navigateToClaim} onNavigateToContact={navigateToContact} onReconciled={refreshReviewCount} /> : null;
      case "sysadmin":    return canSysadminNav ? <SystemAdmin onNavigateToClaim={navigateToClaim} onReconciled={refreshReviewCount} /> : null;
      default: return null;
    }
  }, [user, navigateToClaim, navigateToContact, navigateToCompany, setPage, back, openTime, openTask, bumpWork, workRefresh, refreshReviewCount, canOpsNav, canAccountingNav, canAnalyticsNav, canAdminNav, canSysadminNav]);

  return (
    <>
      <style>{STYLE}</style>
      <AppShell user={user}
                setUser={setUser}
                page={page}
                setPage={setPage}
                reviewCount={reviewCount}
                resetSelection={resetSelection}
                onNavigateToClaim={navigateToClaim}
                onNavigateToBillReview={navigateToBillReview}
                openTime={openTime}
                openTask={openTask}
                workRefresh={workRefresh}>
        <KeepAliveOutlet alive={alive} currentKey={current.key} render={renderPage} />
      </AppShell>
      {timeModal && (
        <TimeEntryModal
          claimId={timeModal.claimId || null}
          claimLabel={timeModal.claimLabel || null}
          adminMode={!!timeModal.adminMode}
          onClose={() => setTimeModal(null)}
          onSaved={bumpWork} />
      )}
      {taskModal && (
        <TaskModal
          claimId={taskModal.claimId || null}
          claimLabel={taskModal.claimLabel || null}
          task={taskModal.task || null}
          onClose={() => setTaskModal(null)}
          onSaved={bumpWork} />
      )}
    </>
  );
}
